import React, {useCallback, useMemo} from 'react';


import {BulkActionDownload} from 'src/components/bulk-tool';
import {useLcdSubscriptionTrees} from 'src/modules/profile/user/tabs/personalization/lcd-credit-analysis/hooks/useLcdSubscriptionTrees';
import {LcdSubscriptions} from 'src/modules/profile/user/tabs/personalization/lcd-credit-analysis/typedef';
import {iterateLcdTreeCount} from 'src/utils/iterateLcdTreeCount';
import {getTreeStructure} from 'src/utils/mapTree';

import CheckBoxTree from '@pb/kit-component-checkbox-tree';
import {Dropdown} from '@pb/kit-component-dropdown';
import {Group} from '@pb/kit-component-group';

import {FILE_HEADERS, LCS_SUBSCRIPTION_TITLES} from './constants';
import {getOptionLabelFromTree} from './utils';

import type {TreeStructure} from '@pb/kit-component-tree-v2';
import type {BulkActionResult} from 'src/components/bulk-tool/typedef';
import type {LcdEnabledItem} from 'src/openapi/admin/models/LcdEnabledItem';
import type {RegionSettings} from 'src/openapi/admin/models/RegionSettings';


type Props = {
    subscribe: boolean,
    bulkResult: BulkActionResult<LcdEnabledItem, {[key: string]: Array<RegionSettings>}>,
};

export const UpdateLcdResultDescription = ({bulkResult, subscribe}: Props) => {
    const {data: trees, isLoading} = useLcdSubscriptionTrees();

    const treeStructures = useMemo<Record<string, TreeStructure>>(() => {
        return trees.reduce((acc, tree) => ({
            ...acc,
            [tree.id]: getTreeStructure(tree, true).map(region => ({
                ...region,
                expanded: true,
            })),
        }), {} as Record<string, TreeStructure>);
    }, [trees]);

    const getSubscriptionCount = useCallback((
        subscriptionType: string,
    ) => {
        const regionSettings = bulkResult.additionalData;
        if (!regionSettings || !Object.keys(treeStructures).length) {
            return 0;
        }

        const selected = regionSettings[subscriptionType].reduce((acc, item) => [
            ...acc,
            ...item.subscriptions.map(subscription => ({region: item.region, subscription})),
        ], [] as {region: string, subscription: string}[]);

        if (!selected.length) {
            return 0;
        }

        const treeStructure = treeStructures[subscriptionType];

        const result: string[] = [];
        treeStructure.forEach(treeItem => iterateLcdTreeCount(
            treeItem,
            result,
            selected,
            treeItem.id,
        ));

        return result.length;
    }, [treeStructures, bulkResult.additionalData]);

    const getSubscriptions = useCallback((
        subscriptionType: LcdSubscriptions,
        regionId: string,
    ) => {
        const regionSettings = bulkResult.additionalData;
        if (!regionSettings) {
            return [];
        }

        const subscriptionPack = regionSettings[subscriptionType];
        const region = subscriptionPack.find(e => e.region === regionId);

        if (region) {
            return region.subscriptions;
        } else {
            return [];
        }
    }, [bulkResult.additionalData]);

    const newsCount = useMemo(
        () => getSubscriptionCount(LcdSubscriptions.NEWS),
        [getSubscriptionCount],
    );

    const researchCount = useMemo(
        () => getSubscriptionCount(LcdSubscriptions.RESEARCH),
        [getSubscriptionCount],
    );

    const downloadMeta = useMemo(() => {
        const result = [
            `${subscribe ? LCS_SUBSCRIPTION_TITLES.SUBSCRIBE : LCS_SUBSCRIPTION_TITLES.UNSUBSCRIBE},,`,
            ',,',
        ];

        const usNews = getSubscriptions(LcdSubscriptions.NEWS, 'US');
        const euNews = getSubscriptions(LcdSubscriptions.NEWS, 'EUROPE');
        if (usNews.length || euNews.length) {
            result.push('News:,,');
            if (usNews.length) {
                const tree = treeStructures[LcdSubscriptions.NEWS]?.find(region => region.id === 'US');
                if (tree && tree.children) {
                    const mapped = tree.children.reduce((acc, child) => [
                        ...acc,
                        ...usNews.map(option => getOptionLabelFromTree(child, option, '')),
                    ], [] as string[]);

                    mapped.filter(Boolean).forEach((option, i) => result.push(`${!i ? 'United States' : ''},${option},`));
                }
            }
            if (euNews.length) {
                const tree = treeStructures[LcdSubscriptions.NEWS]?.find(region => region.id === 'EUROPE');
                if (tree && tree.children) {
                    const mapped = tree.children.reduce((acc, child) => [
                        ...acc,
                        ...euNews.map(option => getOptionLabelFromTree(child, option, '')),
                    ], [] as string[]);

                    mapped.filter(Boolean).forEach((option, i) => result.push(`${!i ? 'Europe' : ''},${option},`));
                }
            }
            result.push(',,');
        }

        const usResearch = getSubscriptions(LcdSubscriptions.RESEARCH, 'US');
        const euResearch = getSubscriptions(LcdSubscriptions.RESEARCH, 'EUROPE');
        if (usResearch.length || euResearch.length) {
            result.push('Research:,,');
            if (usResearch.length) {
                const tree = treeStructures[LcdSubscriptions.RESEARCH]?.find(region => region.id === 'US');
                if (tree && tree.children) {
                    const mapped = tree.children.reduce((acc, child) => [
                        ...acc,
                        ...usResearch.map(option => getOptionLabelFromTree(child, option, '')),
                    ], [] as string[]);

                    mapped.filter(Boolean).forEach((option, i) => result.push(`${!i ? 'United States' : ''},${option},`));
                }
            }
            if (euResearch.length) {
                const tree = treeStructures[LcdSubscriptions.RESEARCH]?.find(region => region.id === 'EUROPE');
                if (tree && tree.children) {
                    const mapped = tree.children.reduce((acc, child) => [
                        ...acc,
                        ...euResearch.map(option => getOptionLabelFromTree(child, option, '')),
                    ], [] as string[]);

                    mapped.filter(Boolean).forEach((option, i) => result.push(`${!i ? 'Europe' : ''},${option},`));
                }
            }
            result.push(',,');
        }

        return result.join('\n');
    }, [subscribe, getSubscriptions, treeStructures]);

    if (isLoading || !trees.length) {
        return <div/>;
    }

    return (
        <Group justify="space-between">
            <b>Result of {subscribe ? 'subscribing to' : 'unsubscribing from'} {
                newsCount ? (
                    <Dropdown
                        maxWidth={0}
                        maxHeight={0}
                        placement="right"
                        hoverEnabled={true}
                        anchor={({anchorRef, getAnchorProps}) => (
                            <span ref={anchorRef} {...getAnchorProps()}>
                                <a>News ({newsCount})</a>
                            </span>
                        )}>
                        <Group style={{height: 300, width: 600}} justify="space-between">
                            {treeStructures[LcdSubscriptions.NEWS]?.map(region => (
                                <div style={{width: 300}} key={region.id}>
                                    <CheckBoxTree
                                        data={[region]}
                                        onChange={() => undefined}
                                        checkedIdsList={getSubscriptions(
                                            LcdSubscriptions.NEWS,
                                            region.id,
                                        )}
                                    />
                                </div>
                            ))}
                        </Group>
                    </Dropdown>
                ) : null
            } {(researchCount && newsCount) ? 'and' : null} {
                researchCount ? (
                    <Dropdown
                        maxWidth={0}
                        maxHeight={0}
                        placement="right"
                        hoverEnabled={true}
                        anchor={({anchorRef, getAnchorProps}) => (
                            <span ref={anchorRef} {...getAnchorProps()}>
                                <a>Research ({researchCount})</a>
                            </span>
                        )}>
                        <Group style={{height: 300, width: 600}} justify="space-between">
                            {treeStructures[LcdSubscriptions.RESEARCH]?.map(region => (
                                <div style={{width: 300}} key={region.id}>
                                    <CheckBoxTree
                                        data={[region]}
                                        onChange={() => undefined}
                                        checkedIdsList={getSubscriptions(
                                            LcdSubscriptions.RESEARCH,
                                            region.id,
                                        )}
                                    />
                                </div>
                            ))}
                        </Group>
                    </Dropdown>
                ) : null
            }
            </b>
            <BulkActionDownload
                toolName={subscribe
                    ? LCS_SUBSCRIPTION_TITLES.SUBSCRIBE
                    : LCS_SUBSCRIPTION_TITLES.UNSUBSCRIBE}
                fileName={subscribe
                    ? 'Subscribe-to-lcd-credit-analysis-report'
                    : 'Unsubscribe-from-lcd-credit-analysis-report'}
                headers={FILE_HEADERS}
                startTime={bulkResult.startTime}
                meta={downloadMeta}
                body={bulkResult.itemList?.map(item => ({
                    ...item,
                    userId: item.userId?.toString(),
                    userEmail: item.userEmail,
                    errors: item.errors?.join('. '),
                })) as Record<string, string>[]}
            />
        </Group>
    );
};
